import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api/axiosConfig';
import { sanitizeHtml } from '../utils/sanitize';
import { renderNoteHtml } from '../utils/noteRichContent';
import NoteModal from './NoteModal';
import { createDocumentFromStoryboard } from '../api/documentApi';
import { Access, canEdit, isOwner } from '../api/sharingApi';
import SharedBanner from './sharing/SharedBanner';
import { useShare } from './sharing/useShare';
import { useLanguage } from '../i18n/LanguageContext';
import { fill } from './sharing/fill';
import './StoryboardEditor.css';

interface Tag {
  id: number;
  name: string;
  color: string;
  text_color: string;
}

interface Entry {
  id: number;
  content: string;
  content_preview: string;
  created_at: string;
  tag_details: Tag[];
  media: any;
  links: any[];
  // On a shared board the cards are several people's notes
  is_own?: boolean;
  owner_name?: string | null;
}

interface StoryboardEntry {
  id: number;
  entry: number;
  entry_data: Entry;
  order: number;
  notes: string;
}

interface Storyboard {
  id: number;
  name: string;
  description: string;
  entries: StoryboardEntry[];
  entry_count: number;
  access?: Access;
}

interface StoryboardEditorProps {
  storyboardId: number;
  onClose: () => void;
}

const StoryboardEditor: React.FC<StoryboardEditorProps> = ({ storyboardId, onClose }) => {
  const { t } = useLanguage();
  const [storyboard, setStoryboard] = useState<Storyboard | null>(null);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Entry[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{
    entry: Entry; x: number; y: number;
  } | null>(null);
  const [isNoteModalOpen, setIsNoteModalOpen] = useState(false);
  const [noteModalTags, setNoteModalTags] = useState<Tag[]>([]);
  // The card note being edited, by storyboard-entry id. A card note says
  // what that beat is doing in this board ("where she realizes he lied") —
  // the writer's own connective tissue, as opposed to the note's content,
  // which is material they saved and may reuse on other boards. Drafting
  // the board carries these in as the spine.
  const [editingNoteId, setEditingNoteId] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  // A card opened to read or edit the note behind it. The note decides for
  // itself what may be done with it: being an editor of a board is not being
  // an editor of everyone's notes on it.
  const [openEntryId, setOpenEntryId] = useState<number | null>(null);
  const [isDocNameOpen, setIsDocNameOpen] = useState(false);
  const [docName, setDocName] = useState('');
  const [isCreatingDoc, setIsCreatingDoc] = useState(false);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const access = storyboard?.access;
  const readOnly = !canEdit(access);
  const owned = isOwner(access);
  const { openShare, shareUi } = useShare('storyboard', storyboardId, storyboard?.name || '');

  // Combine the storyboard into a document: the board's order IS the
  // document order, so this skips the organize step entirely — name it,
  // create it, open it. Each card's note comes along as scaffolding the
  // writer can see while drafting and exports leave behind.
  const handleCreateDocument = async () => {
    if (!storyboard || storyboard.entries.length === 0 || isCreatingDoc) return;
    const name = docName.trim() || storyboard.name;
    setIsCreatingDoc(true);
    try {
      const doc = await createDocumentFromStoryboard(storyboard.id, name);
      // Full navigation so Home boots straight into the open document
      window.location.assign(`/?view=documents&docId=${doc.id}`);
    } catch (error) {
      console.error('Failed to create document from storyboard:', error);
      setError(t.storyboards.docCreateFailed);
      setIsCreatingDoc(false);
      setIsDocNameOpen(false);
    }
  };

  const startEditingCardNote = (storyboardEntryId: number, current: string) => {
    setEditingNoteId(storyboardEntryId);
    setNoteDraft(current || '');
  };

  const saveCardNote = async (storyboardEntryId: number) => {
    const notes = noteDraft.trim();
    setEditingNoteId(null);
    const previous = storyboard?.entries.find(e => e.id === storyboardEntryId)?.notes || '';
    if (notes === previous) return;

    // Optimistic: the note is the writer's train of thought and should not
    // wait on a round trip to appear on the card.
    setStoryboard(current => current && {
      ...current,
      entries: current.entries.map(entry =>
        entry.id === storyboardEntryId ? { ...entry, notes } : entry),
    });
    try {
      await api.patch(`/storyboards/${storyboardId}/update_entry_notes/`, {
        storyboard_entry_id: storyboardEntryId,
        notes,
      });
    } catch (err) {
      console.error('Failed to save card note:', err);
      setError(t.storyboards.noteSaveFailed);
      setStoryboard(current => current && {
        ...current,
        entries: current.entries.map(entry =>
          entry.id === storyboardEntryId ? { ...entry, notes: previous } : entry),
      });
    }
  };

  // Open the add-note modal, optionally pre-tagged with the search query
  // so a "sketch-comedy" search can mint more "sketch-comedy" notes.
  const openNoteModal = (seedFromSearch: boolean) => {
    if (seedFromSearch && searchQuery.trim()) {
      const name = searchQuery.trim().toLowerCase().replace(/\s+/g, '-');
      // Reuse an existing tag's identity/colors when the search already
      // surfaced it; otherwise seed a brand-new tag (temp id).
      const existing = searchResults
        .flatMap(e => e.tag_details)
        .find(t => t.name === name);
      setNoteModalTags([
        existing || { id: Date.now(), name, color: '#5b94f1ff', text_color: '#ffffff' },
      ]);
    } else {
      setNoteModalTags([]);
    }
    setIsNoteModalOpen(true);
  };

  // Plain text of a note without the [[image]] markers — for the compact
  // text strip above the image in cards.
  const previewText = (entry: Entry): string =>
    (entry.content_preview || '')
      .replace(/\[\[image\]\]/g, '')
      .replace(/<[^>]*>/g, '')
      .trim();

  // A PDF is attached the same way a picture is, but is not one to draw
  const imageUrl = (entry: Entry): string | null =>
    entry.media && entry.media.file && entry.media.media_type !== 'pdf'
      ? entry.media.file_url || entry.media.file
      : null;

  const showHoverPreview = (entry: Entry, e: React.MouseEvent) => {
    if (draggedIndex !== null) return;
    // Clamp so the preview stays on screen
    const x = Math.min(e.clientX + 16, window.innerWidth - 380);
    const y = Math.min(e.clientY + 12, window.innerHeight - 420);
    setHoverPreview({ entry, x: Math.max(x, 8), y: Math.max(y, 8) });
  };

  const fetchStoryboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(`/storyboards/${storyboardId}/`);
      setStoryboard(response.data);
    } catch (error) {
      console.error('Failed to fetch storyboard:', error);
      setError(t.storyboards.loadOneFailed);
    } finally {
      setLoading(false);
    }
  }, [storyboardId, t]);

  // Read through a ref: depending on the storyboard itself would give this
  // callback a new identity every time an entry is added, re-running the
  // search effect and throwing the reader back to the top of the results.
  const storyboardEntryIdsRef = useRef<Set<number>>(new Set());
  useEffect(() => {
    storyboardEntryIdsRef.current = new Set(storyboard?.entries.map(e => e.entry) || []);
  }, [storyboard]);

  const searchEntries = useCallback(async () => {
    if (!searchQuery.trim()) return;

    setIsSearching(true);
    try {
      const response = await api.get(`/entries/search/?q=${encodeURIComponent(searchQuery)}`);

      // Filter out entries already in storyboard; list in the order the
      // notes were added (oldest first), not by search relevance —
      // storyboard curation is chronological.
      const existingEntryIds = storyboardEntryIdsRef.current;
      const filtered = response.data
        .filter((entry: Entry) => !existingEntryIds.has(entry.id))
        .sort((a: Entry, b: Entry) =>
          new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
        );

      setSearchResults(filtered);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    fetchStoryboard();
  }, [fetchStoryboard]);

  useEffect(() => {
    if (searchQuery.trim()) {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        searchEntries();
      }, 300);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, searchEntries]);

  const handleAddEntry = async (entryId: number) => {
    // Remove from the list immediately — this also makes a double-click
    // harmless (the second click has no card left to hit).
    setSearchResults(prev => prev.filter(e => e.id !== entryId));
    try {
      await api.post(`/storyboards/${storyboardId}/add_entry/`, {
        entry_id: entryId
      });
      await fetchStoryboard();
    } catch (error: any) {
      const message = error.response?.data?.error;
      if (message === 'Entry already in storyboard') {
        // Benign race (double submit) — the entry is where the user wants it
        fetchStoryboard();
        return;
      }
      console.error('Failed to add entry:', error);
      setError(message || t.storyboards.addEntryFailed);
    }
  };

  const handleRemoveEntry = async (entryId: number) => {
    if (!window.confirm(t.storyboards.removeEntryConfirm)) {
      return;
    }

    try {
      await api.post(`/storyboards/${storyboardId}/remove_entry/`, {
        entry_id: entryId
      });
      
      fetchStoryboard();
    } catch (error) {
      console.error('Failed to remove entry:', error);
      setError(t.storyboards.removeEntryFailed);
    }
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === index || !storyboard) return;

    const entries = [...storyboard.entries];
    const draggedItem = entries[draggedIndex];
    
    entries.splice(draggedIndex, 1);
    entries.splice(index, 0, draggedItem);
    
    setStoryboard({
      ...storyboard,
      entries
    });
    
    setDraggedIndex(index);
  };

  const handleDragEnd = async () => {
    if (!storyboard) return;

    // Update order on backend
    const entryOrders = storyboard.entries.map((entry, index) => ({
      id: entry.id,
      order: index + 1
    }));

    try {
      await api.post(`/storyboards/${storyboardId}/reorder/`, {
        entry_orders: entryOrders
      });
    } catch (error) {
      console.error('Failed to save order:', error);
      setError(t.storyboards.orderSaveFailed);
      fetchStoryboard(); // Reload on error
    }

    setDraggedIndex(null);
  };

  if (loading && !storyboard) {
    return (
      <div className="storyboard-editor">
        <div className="loading">{t.storyboards.loadingOne}</div>
      </div>
    );
  }

  if (!storyboard) {
    return (
      <div className="storyboard-editor">
        <div className="error">{t.storyboards.loadOneFailed}</div>
      </div>
    );
  }

  return (
    <div className="storyboard-editor">
      <div className="storyboard-editor-header">
        <div className="header-left">
          <button className="back-btn" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="19" y1="12" x2="5" y2="12"></line>
              <polyline points="12 19 5 12 12 5"></polyline>
            </svg>
            {t.common.back}
          </button>
          <div className="storyboard-title">
            <h1>{storyboard.name}</h1>
            {storyboard.description && <p>{storyboard.description}</p>}
          </div>
        </div>
        <div className="header-right">
          <span className="entry-count">
            {storyboard.entry_count}{' '}
            {storyboard.entry_count === 1
              ? t.storyboards.entryOne
              : t.storyboards.entryMany}
          </span>
          {owned && (
            <button className="create-doc-btn" onClick={openShare} title={t.sharing.share}>
              👥 {t.sharing.share}
            </button>
          )}
          <button
            className="create-doc-btn"
            disabled={storyboard.entries.length === 0}
            title={t.storyboards.createDocumentTooltip}
            onClick={() => {
              setDocName(storyboard.name);
              setIsDocNameOpen(true);
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
            </svg>
            {t.storyboards.createDocument}
          </button>
        </div>
      </div>

      <SharedBanner access={access} />

      {error && <div className="error-message">{error}</div>}

      <div className={`storyboard-content ${isPanelCollapsed ? 'panel-collapsed' : ''}`}>
        {/* Adding notes is an editor's doing; a viewer just reads the board */}
        {!readOnly && <div className={`search-panel ${isPanelCollapsed ? 'collapsed' : ''}`}>
          <div className="search-panel-header">
            <h2>{t.storyboards.addEntries}</h2>
            <button 
              className="collapse-btn" 
              onClick={() => setIsPanelCollapsed(!isPanelCollapsed)}
              title={isPanelCollapsed ? t.storyboards.expand : t.storyboards.collapse}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                {isPanelCollapsed ? (
                  <polyline points="9 18 15 12 9 6"></polyline>
                ) : (
                  <polyline points="15 18 9 12 15 6"></polyline>
                )}
              </svg>
            </button>
          </div>
          <div className="search-box">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t.storyboards.searchPlaceholder}
              className="search-input"
            />
          </div>
          
          <div className="search-results">
            {searchQuery.trim() && !isSearching && (
              <button
                className="new-note-tagged-btn"
                onClick={() => openNoteModal(true)}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
                {fill(t.storyboards.newNoteTagged, {
                  tag: searchQuery.trim().toLowerCase().replace(/\s+/g, '-'),
                })}
              </button>
            )}
            {isSearching && searchResults.length === 0 ? (
              <div className="searching">{t.storyboards.searching}</div>
            ) : searchResults.length > 0 ? (
              searchResults.map((entry) => {
                const text = previewText(entry);
                const img = imageUrl(entry);
                return (
                  <div key={entry.id} className="search-result-item">
                    {text && (
                      <div className="entry-preview-text">{text}</div>
                    )}
                    {img ? (
                      <div className="entry-preview-image">
                        <img src={img} alt="" loading="lazy" />
                      </div>
                    ) : !text ? (
                      <div className="entry-preview-text entry-preview-empty">{t.storyboards.noContent}</div>
                    ) : null}
                    <div className="entry-actions">
                      <button
                        className="add-entry-circle"
                        title={t.storyboards.addToStoryboard}
                        onClick={() => handleAddEntry(entry.id)}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                          <line x1="12" y1="5" x2="12" y2="19"></line>
                          <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                      </button>
                      {entry.tag_details.length > 0 && (
                      <div className="entry-tags-hover">
                        <div className="entry-tags-trigger" title={t.note.tags}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
                            <line x1="7" y1="7" x2="7.01" y2="7"></line>
                          </svg>
                        </div>
                        <div className="entry-tags-popup">
                          {entry.tag_details.map((tag) => (
                            <span
                              key={tag.id}
                              className="tag-badge"
                              style={{ backgroundColor: tag.color, color: tag.text_color }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : searchQuery ? (
              <div className="no-results">{t.home.noEntries}</div>
            ) : (
              <div className="search-prompt">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="8"></circle>
                  <path d="m21 21-4.35-4.35"></path>
                </svg>
                <p>{t.storyboards.searchPrompt}</p>
                <p className="add-first-note-hint">
                  {t.storyboards.searchPromptHint}
                </p>
                <button
                  className="add-first-note-btn"
                  onClick={() => openNoteModal(false)}
                >
                  {t.nav.addNote}
                </button>
              </div>
            )}
          </div>
        </div>}

        <div className="storyboard-panel">
          <h2>{t.storyboards.boardHeading}</h2>
          {storyboard.entries.length === 0 ? (
            <div className="empty-storyboard">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="7" height="7"></rect>
                <rect x="14" y="3" width="7" height="7"></rect>
                <rect x="14" y="14" width="7" height="7"></rect>
                <rect x="3" y="14" width="7" height="7"></rect>
              </svg>
              <p>{t.storyboards.empty}</p>
              <p className="hint">{t.storyboards.emptyHint}</p>
            </div>
          ) : (
            <div className="storyboard-entries-grid">
              {storyboard.entries.map((storyboardEntry, index) => (
                <div
                  key={storyboardEntry.id}
                  className={`storyboard-entry ${draggedIndex === index ? 'dragging' : ''}${
                    storyboardEntry.entry_data.is_own === false ? ' not-mine' : ''}`}
                  draggable={!readOnly}
                  onDragStart={() => { if (readOnly) return; setHoverPreview(null); handleDragStart(index); }}
                  onDragOver={(e) => { if (!readOnly) handleDragOver(e, index); }}
                  onDragEnd={() => { if (!readOnly) handleDragEnd(); }}
                  onMouseEnter={(e) => showHoverPreview(storyboardEntry.entry_data, e)}
                  onMouseMove={(e) => showHoverPreview(storyboardEntry.entry_data, e)}
                  onMouseLeave={() => setHoverPreview(null)}
                >
                  <div className="entry-order">{index + 1}</div>
                  {storyboardEntry.entry_data.owner_name && (
                    <span className="card-owner">
                      {fill(t.sharing.othersNote, { name: storyboardEntry.entry_data.owner_name })}
                    </span>
                  )}
                  <div
                    className="storyboard-card-content"
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenEntryId(storyboardEntry.entry)}
                    onKeyDown={(e) => { if (e.key === 'Enter') setOpenEntryId(storyboardEntry.entry); }}
                  >
                    {imageUrl(storyboardEntry.entry_data) ? (
                      <div className="storyboard-card-image">
                        <img src={imageUrl(storyboardEntry.entry_data)!} alt="" loading="lazy" />
                      </div>
                    ) : (
                      <div className="storyboard-card-text" dangerouslySetInnerHTML={{ __html: sanitizeHtml(storyboardEntry.entry_data.content_preview) }} />
                    )}
                  </div>
                  {readOnly ? (
                    storyboardEntry.notes ? (
                      <span className="card-note">{storyboardEntry.notes}</span>
                    ) : null
                  ) : editingNoteId === storyboardEntry.id ? (
                    <textarea
                      className="card-note-input"
                      value={noteDraft}
                      autoFocus
                      placeholder={t.storyboards.cardNotePlaceholder}
                      onChange={(e) => setNoteDraft(e.target.value)}
                      onBlur={() => saveCardNote(storyboardEntry.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') setEditingNoteId(null);
                        // Enter saves; the note is a line, not a paragraph
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          saveCardNote(storyboardEntry.id);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onMouseEnter={() => setHoverPreview(null)}
                    />
                  ) : (
                    <button
                      className={`card-note ${storyboardEntry.notes ? '' : 'empty'}`}
                      title={t.storyboards.cardNoteTitle}
                      onClick={(e) => {
                        e.stopPropagation();
                        startEditingCardNote(storyboardEntry.id, storyboardEntry.notes);
                      }}
                    >
                      {storyboardEntry.notes || t.storyboards.addCardNote}
                    </button>
                  )}
                  {!readOnly && (
                    <>
                      <button 
                        className="storyboard-delete-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveEntry(storyboardEntry.entry);
                        }}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="18" y1="6" x2="6" y2="18"></line>
                          <line x1="6" y1="6" x2="18" y2="18"></line>
                        </svg>
                      </button>
                      <div className="drag-handle">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="3" y1="12" x2="21" y2="12"></line>
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <line x1="3" y1="18" x2="21" y2="18"></line>
                        </svg>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {isDocNameOpen && (
        <div className="doc-name-overlay" onClick={() => !isCreatingDoc && setIsDocNameOpen(false)}>
          <div className="doc-name-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>{t.storyboards.docDialogTitle}</h3>
            <p className="doc-name-hint">
              {fill(t.storyboards.docDialogHint, {
                count: String(storyboard.entries.length),
              })}
            </p>
            <input
              type="text"
              className="doc-name-input"
              value={docName}
              autoFocus
              placeholder={t.storyboards.docNamePlaceholder}
              onChange={(e) => setDocName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateDocument(); }}
            />
            <div className="doc-name-actions">
              <button
                className="doc-name-cancel"
                disabled={isCreatingDoc}
                onClick={() => setIsDocNameOpen(false)}
              >
                {t.common.cancel}
              </button>
              <button
                className="doc-name-create"
                disabled={isCreatingDoc}
                onClick={handleCreateDocument}
              >
                {isCreatingDoc ? t.storyboards.creating : t.storyboards.createAndOpen}
              </button>
            </div>
          </div>
        </div>
      )}

      <NoteModal
        isOpen={isNoteModalOpen}
        onClose={() => setIsNoteModalOpen(false)}
        onSuccess={() => {
          setIsNoteModalOpen(false);
          // Surface the new note in the results right away
          if (searchQuery.trim()) searchEntries();
        }}
        initialTags={noteModalTags}
      />

      {openEntryId !== null && (
        <NoteModal
          isOpen
          noteId={openEntryId}
          onClose={() => setOpenEntryId(null)}
          onSuccess={() => { setOpenEntryId(null); fetchStoryboard(); }}
        />
      )}

      {shareUi}

      {/* Full-card hover preview (like Discover's node tooltips): the whole
          note — image, complete text, tags — enlarged, following the cursor. */}
      {hoverPreview && (
        <div
          className="storyboard-hover-preview"
          style={{ left: hoverPreview.x, top: hoverPreview.y }}
        >
          {imageUrl(hoverPreview.entry) && (
            <img
              className="storyboard-hover-image"
              src={imageUrl(hoverPreview.entry)!}
              alt=""
            />
          )}
          {previewText(hoverPreview.entry) && (
            <div
              className="storyboard-hover-text"
              dangerouslySetInnerHTML={{
                __html: renderNoteHtml(
                  hoverPreview.entry.content.replace(/\[\[image\]\]/g, '')
                ),
              }}
            />
          )}
          {hoverPreview.entry.tag_details.length > 0 && (
            <div className="storyboard-hover-tags">
              {hoverPreview.entry.tag_details.map((tag) => (
                <span
                  key={tag.id}
                  className="tag-badge"
                  style={{ backgroundColor: tag.color, color: tag.text_color }}
                >
                  {tag.name}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StoryboardEditor;
